import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@chakra-ui/react";
import { FaAngleLeft } from "react-icons/fa";
import axios from "axios";
import SweetAlert2 from "react-sweetalert2";
import Card from "components/card";
import InputField from "components/fields/InputField";
import FeeTemplateSection from "./components/FeeTemplateSection";
import { getFeeTypeDisplayName } from "../utils/feeTypeDisplayNames";

type FeeTemplate = {
  feetemplate_id: string;
  asset: string;
  asset_type: string;
  type: string;
  fixed: string;
  percentage: string;
  min_amount: string;
  max_amount: string;
  is_active: boolean;
  decimal: number;
};

const formatDecimal = (value: string, decimals: number): string => {
  if (!value || value === "") return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(decimals);
};

type FeeGroupData = {
  feegroup_id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  created_date: string;
};

const FeeGroupAgentDetail = ({ userPermissions }: { userPermissions: any }) => {
  const { feegroup_id } = useParams();
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase() || "";

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [feeGroup, setFeeGroup] = useState<FeeGroupData | null>(null);
  const [feeTemplates, setFeeTemplates] = useState<FeeTemplate[]>([]);
  const [originalFeeGroup, setOriginalFeeGroup] = useState<FeeGroupData | null>(
    null
  );
  const [originalFeeTemplates, setOriginalFeeTemplates] = useState<
    FeeTemplate[]
  >([]);
  const [swalProps, setSwalProps] = useState<any>({});
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>(
    {}
  );

  useEffect(() => {
    if (feegroup_id) {
      fetchFeeGroupDetail();
    }
  }, [feegroup_id]);

  const areAllOpen =
    Object.keys(openSections).length > 0 &&
    Object.values(openSections).every(Boolean);

  const toggleAllSections = () => {
    const newState = !areAllOpen;
    setOpenSections(
      Object.keys(openSections).reduce(
        (acc, key) => ({ ...acc, [key]: newState }),
        {}
      )
    );
  };

  const fetchFeeGroupDetail = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/feegroup/feegroup-detail?feegroup_id=${feegroup_id}&type=agent`
      );

      const data = response.data?.feegroup?.payload;

      // Check if fee group data exists, if not redirect to fee group listing
      if (!data) {
        console.error("Agent fee group not found");
        navigate(`/${userRole}/fee-groups/agent`);
        return;
      }

      if (data) {
        setFeeGroup(data.feegroup);
        setOriginalFeeGroup(JSON.parse(JSON.stringify(data.feegroup)));

        // Format templates with proper decimal places
        const formattedTemplates = (data.feetemplates || []).map(
          (t: FeeTemplate) => ({
            ...t,
            fixed: formatDecimal(t.fixed, t.decimal),
            percentage: formatDecimal(t.percentage, 2),
          })
        );
        setFeeTemplates(formattedTemplates);
        setOriginalFeeTemplates(JSON.parse(JSON.stringify(formattedTemplates)));

        // Initialize openSections with first type open
        const types: string[] = [
          ...new Set((data.feetemplates || []).map((t: FeeTemplate) => t.type)),
        ].sort() as string[];
        const initialOpenState: { [key: string]: boolean } = {};
        types.forEach((type, index) => {
          initialOpenState[type] = index === 0;
        });
        setOpenSections(initialOpenState);
      }
    } catch (error: any) {
      console.error("Error fetching fee group detail:", error);
      // Redirect to agent fee group listing page on error
      navigate(`/${userRole}/fee-groups/agent`);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Failed to load fee group details",
        showConfirmButton: true,
        didClose: () => setSwalProps({}),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeeGroupChange = (field: string, value: string) => {
    setFeeGroup((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleTemplateChange = (
    templateId: string,
    field: string,
    value: string
  ) => {
    setFeeTemplates((prev) =>
      prev.map((template) =>
        template.feetemplate_id === templateId
          ? { ...template, [field]: value }
          : template
      )
    );
  };

  const getChangedTemplates = () => {
    return feeTemplates.filter((template, index) => {
      const original = originalFeeTemplates[index];
      if (!original) return true;
      return (
        template.fixed !== original.fixed ||
        template.percentage !== original.percentage ||
        template.min_amount !== original.min_amount ||
        template.max_amount !== original.max_amount
      );
    });
  };

  const handleSave = async () => {
    setIsUpdating(true);

    try {
      let updateCount = 0;
      const errors: string[] = [];

      // Update fee group name/description if changed
      if (
        feeGroup &&
        originalFeeGroup &&
        (feeGroup.name !== originalFeeGroup.name ||
          feeGroup.description !== originalFeeGroup.description)
      ) {
        try {
          const response = await axios.post(
            process.env.REACT_APP_API_URL +
              `/api/${userRole}/feegroup/action/update-feegroup?type=agent`,
            {
              feegroup_id: feeGroup.feegroup_id,
              name: feeGroup.name,
              description: feeGroup.description,
              feegroup_data: null,
              __with_default_feetemplate: false,
              username: localStorage.getItem("username"),
              userRole: userRole,
            }
          );
          if (response && response.data?.status === "ok") {
            updateCount++;
          } else {
            errors.push(
              response.data?.message || "Failed to update fee group info"
            );
          }
        } catch (error: any) {
          errors.push(
            error.response?.data?.message || "Failed to update fee group info"
          );
        }
      }

      // Update changed fee templates
      const changedTemplates = getChangedTemplates();
      for (const template of changedTemplates) {
        try {
          const response = await axios.post(
            process.env.REACT_APP_API_URL +
              `/api/${userRole}/feegroup/action/update-fee-template`,
            {
              feetemplate_id: template.feetemplate_id,
              feegroup_id: feegroup_id,
              type: template.type,
              asset: template.asset,
              asset_type: template.asset_type,
              fixed: parseFloat(template.fixed) || 0,
              percentage: parseFloat(template.percentage) || 0,
              max_amount: parseFloat(template.max_amount) || 0,
              min_amount: parseFloat(template.min_amount) || 0,
              username: localStorage.getItem("username"),
              userRole: userRole,
            }
          );
          if (response && response.data?.status === "ok") {
            updateCount++;
          } else {
            errors.push(
              response.data?.message ||
                `Failed to update ${template.asset} ${template.type}`
            );
          }
        } catch (error: any) {
          errors.push(
            error.response?.data?.message ||
              `Failed to update ${template.asset} ${template.type}`
          );
        }
      }

      // Show result
      if (errors.length > 0) {
        setSwalProps({
          show: true,
          icon: "warning",
          title: "Partially Updated",
          html: `${updateCount} item(s) updated successfully.<br/>Errors:<br/>${errors.join(
            "<br/>"
          )}`,
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsUpdating(false);
          },
        });
      } else {
        setSwalProps({
          show: true,
          icon: "success",
          title: "Success",
          html: "Fee group updated successfully",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsUpdating(false);
            fetchFeeGroupDetail();
          },
        });
      }
    } catch (error: any) {
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html:
          error.response?.data?.message ||
          "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsUpdating(false);
        },
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const groupedTemplates = useMemo(() => {
    const groups: { [key: string]: FeeTemplate[] } = {};
    feeTemplates.forEach((template) => {
      if (!groups[template.type]) {
        groups[template.type] = [];
      }
      groups[template.type].push(template);
    });
    return groups;
  }, [feeTemplates]);

  const isFormValid = feeGroup?.name && feeGroup?.description;

  if (isLoading) {
    return (
      <>
        {/* Header skeleton */}
        <Card className="card card-pad !rounded-xl">
          <div className="mb-3 flex items-center">
            <div
              className="h-7 w-48 animate-pulse rounded bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            />
          </div>

          <div
            className="mb-4 h-6 w-48 animate-pulse rounded bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <div
                className="mb-2 h-4 w-16 animate-pulse rounded bg-gray-200"
                style={{ animationDuration: "0.6s" }}
              />
              <div
                className="h-10 w-full animate-pulse rounded-md bg-gray-200"
                style={{ animationDuration: "0.6s" }}
              />
            </div>
            <div>
              <div
                className="mb-2 h-4 w-24 animate-pulse rounded bg-gray-200"
                style={{ animationDuration: "0.6s" }}
              />
              <div
                className="h-10 w-full animate-pulse rounded-md bg-gray-200"
                style={{ animationDuration: "0.6s" }}
              />
            </div>
          </div>
          {/* Fee Templates skeleton */}
          <div className="mt-6">
            {[1].map((i) => (
              <Card key={i} className="card mb-4 !rounded-xl p-6">
                <div
                  className="mb-4 h-5 w-32 animate-pulse rounded bg-gray-200"
                  style={{ animationDuration: "0.6s" }}
                />
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5, 6].map((j) => (
                    <div key={j} className="grid grid-cols-4 gap-4">
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                      <div
                        className="h-10 animate-pulse rounded-md bg-gray-200"
                        style={{ animationDuration: "0.6s" }}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          {/* Save button skeleton */}
          <div className="mt-6 flex justify-end">
            <div
              className="h-10 w-24 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            />
          </div>
        </Card>
      </>
    );
  }

  if (!feeGroup) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">Fee group not found</div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <Card className="card card-pad !rounded-xl">
        <div className="mb-3 flex items-center">
          <div>
            <button
              type="button"
              onClick={() => navigate(`/${userRole}/fee-groups/agent`)}
              disabled={isUpdating}
              className="
                mb-1 inline-flex items-center gap-1
                text-sm font-medium text-gray-700
                transition hover:text-gray-700
                disabled:cursor-not-allowed disabled:opacity-40
                dark:text-gray-400 dark:hover:text-white
              "
            >
              <FaAngleLeft className="text-base" />
              <span>Agent Fee Group</span>
            </button>

            <h3 className="text-xl font-semibold text-navy-700">
              {feeGroup.name}
            </h3>
          </div>
        </div>
        <div className="my-4 text-sm text-navy-700 dark:text-white">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <InputField
                id="name"
                label="Name"
                placeholder="Enter name"
                value={feeGroup.name}
                onChange={(e) => handleFeeGroupChange("name", e.target.value)}
                disabled={isUpdating}
                variant="auth"
                extra="w-full"
                type="text"
                required
              />
            </div>
            <div>
              <InputField
                id="description"
                label="Description"
                placeholder="Enter description"
                value={feeGroup.description}
                onChange={(e) =>
                  handleFeeGroupChange("description", e.target.value)
                }
                disabled={isUpdating}
                variant="auth"
                extra="w-full"
                type="text"
                required
              />
            </div>
          </div>
        </div>

        <hr className="my-10" />

        {Object.keys(groupedTemplates).length > 0 ? (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-lg font-medium text-navy-700 dark:text-white">
                  Fee Templates
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-white/60">
                  Manage templates by fee type. Expand sections to review and
                  edit.
                </p>
              </div>

              <button
                type="button"
                onClick={toggleAllSections}
                className="dark:bg-neutral-900 shrink-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-navy-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
              >
                {areAllOpen ? (
                  <span className="flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
                    Collapse all
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-primary-500" />
                    Show all
                  </span>
                )}
              </button>
            </div>

            <div className="py-3 md:py-4">
              <div className="space-y-3">
                {Object.keys(groupedTemplates)
                  .sort()
                  .map((type) => (
                    <FeeTemplateSection
                      key={type}
                      title={getFeeTypeDisplayName(type)}
                      templates={groupedTemplates[type]}
                      onTemplateChange={handleTemplateChange}
                      isUpdating={isUpdating}
                      isOpen={openSections[type] ?? false}
                      onToggle={() =>
                        setOpenSections((prev) => ({
                          ...prev,
                          [type]: !prev[type],
                        }))
                      }
                    />
                  ))}
              </div>
            </div>
          </>
        ) : (
          <div className="dark:bg-neutral-900 mb-4 rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center dark:border-white/10">
            <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-gray-100 dark:bg-white/5" />
            <p className="text-sm font-medium text-navy-700 dark:text-white">
              No fee templates yet
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-white/60">
              Once templates are created, they’ll appear here grouped by fee
              type.
            </p>
          </div>
        )}

        <div className="mb-4 flex justify-end">
          <Button
            onClick={handleSave}
            isDisabled={!isFormValid || isUpdating}
            isLoading={isUpdating}
            loadingText="Saving..."
            className="btn-primary relative overflow-hidden rounded-md px-8 py-2.5 font-semibold text-white shadow-md"
          >
            <span className="relative z-10 tracking-tight">Save</span>
          </Button>
        </div>
      </Card>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      />
    </div>
  );
};

export default FeeGroupAgentDetail;
